import {
  Avatar,
  Badge,
  Divider,
  Icon,
  IconNames,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import * as Popover from '@radix-ui/react-popover';
import { SearchUserEnriched } from 'lib-search-types';
import { useHasAccess } from 'lib-shared-access-control';
import { convertChannelCasing } from 'lib-shared-api-utils';
import type { LoadDescriptionT, Presence } from 'lib-shared-legacy-types';
import { User } from 'lib-shared-types-api';
import { Channel } from 'lib-shared-types-channels';
import capitalize from 'lodash/capitalize';
import moment from 'moment-timezone';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { presenceApi } from 'apis/presence';
import {
  Presence as ApiPresence,
  PresenceLoadDescription,
} from 'apis/presence/types/presence.types';
import { twilioActions } from 'app/middleware/twilio/actions/twilioActions';
import { presenceActions } from 'components/presence/actions/presenceActions';
import {
  PresenceDropdownItem,
  PresenceToggle,
} from 'components/profile/components/PresenceDropdownItem';
import { userModalActions } from 'components/usersModal';
import { channelAttributes } from 'utils/channels';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { bindActionToPromise } from 'utils/sagaApiGeneratorHelpers';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import { getStatus } from '../utils';

import PresenceOverlayChannels from './PresenceOverlayChannels';
import styles from './presenceOverlayPopoverNew.module.scss';

type PresenceOverlayPopoverNewProps = {
  member: SearchUserEnriched;
  ownProfile: User;
  isFavorite: boolean;
  onAddToFavorites: (userId: string) => void;
  onRemoveFromFavorites: (userId: string) => void;
  children: ReactNode;
};

const hasActivePhoneConversation = (
  loadDescriptions: LoadDescriptionT[],
): boolean =>
  loadDescriptions.some(
    (o) =>
      (o.conversation_channel === 'pstnphone' ||
        o.conversation_channel === 'callback') &&
      o.load_type !== 'acw' &&
      o.load_type !== 'nonlive',
  );

export function computeInternalCallState(
  presence: ApiPresence | Presence,
  ownPresence: Presence,
  ownAgentId: string,
  isOwnAgent: boolean,
  canMakeInternalCall: boolean,
) {
  let isConnectedWithOwnAgent = false;
  let isInternalCallCalling = false;
  let internalBtnText = 'Call';

  const internalCalls = presence.presence_status.load_descriptions.filter(
    (o) =>
      o.load_type === 'internalwork' && o.conversation_channel === 'pstnphone',
  );

  const ownInternalCalls = ownPresence.presence_status.load_descriptions.filter(
    (o) =>
      o.load_type === 'internalwork' && o.conversation_channel === 'pstnphone',
  );

  const numberOfAgentsActiveCalls =
    presence.presence_status.load_descriptions.filter(
      (o) =>
        (o.conversation_channel === 'pstnphone' ||
          o.conversation_channel === 'callback') &&
        o.load_type !== 'acw' &&
        o.load_type !== 'nonlive',
    ).length;

  for (const ownInternalCall of ownInternalCalls) {
    for (const internalCall of internalCalls) {
      if (ownInternalCall.csid === internalCall.csid) {
        isConnectedWithOwnAgent = true;
        internalBtnText = 'Connected';

        if (
          ownInternalCall.requester !== null &&
          ownInternalCall.requester.id === ownAgentId
        ) {
          if (internalCall?.conversation?.assignment !== null) {
            // Internal was started by own and accepted - show connected.
          } else {
            // Internal was started by own - show calling at btn
            isInternalCallCalling = true;
            internalBtnText = 'Connected';
          }
          break;
        }
        break;
      }
    }
  }

  const isCallDisabled =
    canMakeInternalCall === false ||
    isConnectedWithOwnAgent === true ||
    isOwnAgent === true ||
    numberOfAgentsActiveCalls > 0 ||
    presence.status === 'offline' ||
    isInternalCallCalling === true;

  return { internalBtnText, isCallDisabled };
}

function prepareConversationsByChannel(
  loadDescriptions: readonly PresenceLoadDescription[] | LoadDescriptionT[],
) {
  // Type assertion needed because API returns readonly arrays but PresenceOverlay uses mutable arrays
  const descriptions = loadDescriptions as LoadDescriptionT[];

  const acwConversations: LoadDescriptionT[] = [];
  const grouped: Partial<Record<IconNames, LoadDescriptionT[]>> = {};

  for (const description of descriptions) {
    if (description.load_type === 'acw') {
      acwConversations.push(description);
      continue;
    }

    const channel = convertChannelCasing(
      description.conversation_channel,
      'snake',
    ) as Channel;
    const icon: IconNames =
      channel in channelAttributes
        ? channelAttributes[channel].icon
        : 'messenger';

    // Skip internal phone calls (shown separately via internal call UI)
    if (icon === 'phone' && description.load_type === 'internalwork') {
      continue;
    }

    if (!grouped[icon]) {
      grouped[icon] = [];
    }
    grouped[icon].push(description);
  }

  return {
    conversationsByChannel: {
      clock: acwConversations,
      ...grouped,
    } as Partial<Record<IconNames, LoadDescriptionT[]>>,
    acwConversations,
  };
}

const presenceBadgeMap: Record<
  Presence['status'],
  'success' | 'warning' | 'secondary'
> = {
  working: 'success',
  away: 'warning',
  offline: 'secondary',
};

export const PresenceOverlayPopoverNew = ({
  member,
  ownProfile,
  isFavorite,
  onAddToFavorites,
  onRemoveFromFavorites,
  children,
}: PresenceOverlayPopoverNewProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const isOwnAgent = ownProfile.id === member.id;
  const isOffline = getStatus(member) === 'offline';

  const ownPresence = useAppSelector((state) => state.presence.presence);
  const isInternalCallDialing = useAppSelector(
    (state) => state.twilio.meta.internalCall === 'loading',
  );
  const isDialingThisMember = useAppSelector(
    (state) =>
      state.twilio.meta.internalCall === 'loading' &&
      state.twilio.pendingInternalCallUserId === member.id,
  );
  const isSimultaneousRingLoading = useAppSelector(
    (state) => state.presence.meta.toggleSimultaneousRing === 'loading',
  );
  const isMicrophoneBlocked = useAppSelector(
    (state) => state.telephony.isMicrophoneBlocked,
  );

  const featureFlags = useFeatureFlag(['phone-channel']);
  const hasChannelPhone = featureFlags.phoneChannel;

  const hasOrganizationMemberAccess = useHasAccess('organization.member');
  const hasConversationReplyAccess = useHasAccess('conversation.reply');

  const [
    triggerFetch,
    { data: presenceData, isLoading, isFetching, isUninitialized },
  ] = presenceApi.useLazyReadAgentPresenceQuery();

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
  }, []);

  // Refetching on own internal call changes keeps the call button in sync
  // with the live call state (Call/Calling/Connected), since the member's
  // presence is otherwise only as fresh as the last popover open.
  const ownInternalCallCsids = ownPresence.presence_status.load_descriptions
    .filter(
      (o) =>
        o.load_type === 'internalwork' &&
        o.conversation_channel === 'pstnphone',
    )
    .map((o) => o.csid)
    .join();

  useEffect(() => {
    if (isOpen) {
      triggerFetch({ id: member.id });
    }
  }, [isOpen, ownInternalCallCsids, member.id, triggerFetch]);

  const canMakeInternalCall = useMemo(() => {
    return (
      !isMicrophoneBlocked &&
      !hasActivePhoneConversation(ownPresence.presence_status.load_descriptions)
    );
  }, [isMicrophoneBlocked, ownPresence]);

  const { internalBtnText, isCallDisabled } = useMemo(() => {
    if (!presenceData?.data?.presence) {
      return {
        internalBtnText: 'Call',
        isCallDisabled:
          isOffline ||
          isOwnAgent ||
          !canMakeInternalCall ||
          isInternalCallDialing,
      };
    }

    const state = computeInternalCallState(
      presenceData.data.presence,
      ownPresence,
      ownProfile.id,
      isOwnAgent,
      canMakeInternalCall,
    );
    return {
      ...state,
      isCallDisabled: state.isCallDisabled || isInternalCallDialing,
    };
  }, [
    presenceData,
    ownPresence,
    ownProfile.id,
    isOwnAgent,
    canMakeInternalCall,
    isOffline,
    isInternalCallDialing,
  ]);

  const onInternalCallClickHandler = useCallback(() => {
    if (!isCallDisabled && presenceData?.data?.user) {
      bindActionToPromise(
        dispatch,
        twilioActions.twilioMakeInternalCall,
      )({
        userId: presenceData.data.user.id,
      });
    }
  }, [isCallDisabled, presenceData?.data?.user, dispatch]);

  const handlePresenceLinkClick = useCallback(
    (csid: LoadDescriptionT['csid']) => {
      navigate({
        path: '/conversation/:csid',
        params: { path: { csid: `${csid}` } },
      });
    },
    [navigate],
  );

  const presenceSetOthersSimultaneousRinging = useCallback(
    (payload: { userId: string; enabled: boolean }) => {
      return bindActionToPromise(
        dispatch,
        presenceActions.presenceSetOthersSimultaneousRinging,
      )(payload);
    },
    [dispatch],
  );

  const showUserModal = useCallback(
    (payload: { user: User }) => {
      dispatch(userModalActions.show(payload));
    },
    [dispatch],
  );

  const {
    conversationsByChannel,
    lastSeen,
    acwConversationNode,
    hasPhone,
    presence,
    user,
  } = useMemo(() => {
    if (!presenceData?.data) {
      return {
        conversationsByChannel: {} as Partial<
          Record<IconNames, LoadDescriptionT[]>
        >,
        lastSeen: null,
        acwConversationNode: null,
        hasPhone: false,
        presence: null,
        user: null,
      };
    }

    const { presence, user } = presenceData.data;
    const { conversationsByChannel, acwConversations } =
      prepareConversationsByChannel(presence.presence_status.load_descriptions);

    const numberOfAcw = acwConversations.length;
    let lastSeen: string | null = null;
    let acwConversationNode: JSX.Element | null = null;

    if (numberOfAcw > 0) {
      const conversation = numberOfAcw === 1 ? 'conversation' : 'conversations';
      acwConversationNode = (
        <span>
          Wrap-up time on {numberOfAcw} {conversation}
        </span>
      );
    }

    if (presence.status === 'offline') {
      lastSeen =
        presence.last_seen === null
          ? 'Has not signed-in yet'
          : `Last seen ${moment(presence.last_seen).fromNow()}`;
    }

    const hasPhone = !!user.phone_number && user.phone_number.length > 0;

    return {
      conversationsByChannel,
      lastSeen,
      acwConversationNode,
      hasPhone,
      presence,
      user,
    };
  }, [presenceData]);

  const [simRingEnabled, setSimRingEnabled] = useState(false);

  useEffect(() => {
    setSimRingEnabled(!!presence?.presence_status.simultaneous_ringing);
  }, [presence?.presence_status.simultaneous_ringing]);

  const hasConversationsAssigned = Object.keys(conversationsByChannel).some(
    (channel) =>
      (conversationsByChannel as Record<string, LoadDescriptionT[]>)[channel]
        ?.length > 0,
  );

  const handleToggleSimultaneousRing = useCallback(async () => {
    if (!hasPhone || isSimultaneousRingLoading || !user) {
      return;
    }
    setSimRingEnabled((prevState) => !prevState);

    try {
      await presenceSetOthersSimultaneousRinging({
        userId: user.id,
        enabled: !simRingEnabled,
      });
    } catch {
      setSimRingEnabled((prevState) => !prevState);
    }
  }, [
    hasPhone,
    isSimultaneousRingLoading,
    user,
    presenceSetOthersSimultaneousRinging,
    simRingEnabled,
  ]);

  return (
    <Popover.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>{children}</Popover.Trigger>

      <Popover.Portal>
        <Popover.Content className={styles.dropdown} side="right">
          {isUninitialized || isLoading || (isFetching && !presenceData) ? (
            <div className={styles.loadingContainer}>
              <Spinner size={24} />
            </div>
          ) : presence && user ? (
            <>
              <div className={styles.header}>
                <Avatar user={user} />
                <Text.Paragraph className={styles.name} as="span" variant="2">
                  {user.name !== undefined && user.name !== null
                    ? user.name.replace(/ .*/, '')
                    : ''}
                </Text.Paragraph>
                {presence.status && (
                  <Badge
                    variant={presenceBadgeMap[presence.status] ?? 'secondary'}
                    size="medium"
                  >
                    {capitalize(presence.status)}
                  </Badge>
                )}
              </div>

              {/* Type assertion needed: API Presence type is structurally compatible with legacy Presence */}
              <PresenceOverlayChannels presence={presence as Presence} />

              <Divider />

              <div>
                {[acwConversationNode, lastSeen]
                  .filter(Boolean)
                  .map((item, index) => (
                    <div key={index}>
                      <Text.Paragraph
                        variant="2"
                        as="div"
                        color="tertiary"
                        padding="200 0"
                        align="center"
                      >
                        {item}
                      </Text.Paragraph>
                      <Divider />
                    </div>
                  ))}

                {hasChannelPhone && (
                  <>
                    {hasConversationReplyAccess &&
                      hasOrganizationMemberAccess && (
                        <PresenceDropdownItem
                          icon="simultaneous"
                          text="Simultaneous ring"
                          tooltipText={
                            hasPhone
                              ? `Toggle simultaneous ringing for ${
                                  user && user.name ? user.name : 'agent'
                                }`
                              : 'Simultaneous ringing is turned off (phone number needed)'
                          }
                          disabled={!hasPhone}
                        >
                          <PresenceToggle
                            id="shouldOpenSimultaneous"
                            disabled={hasPhone && isSimultaneousRingLoading}
                            active={simRingEnabled}
                            onToggle={handleToggleSimultaneousRing}
                          />
                        </PresenceDropdownItem>
                      )}

                    {hasConversationReplyAccess && !isOwnAgent && (
                      <PresenceDropdownItem
                        icon="phone"
                        text={
                          isDialingThisMember ? 'Calling...' : internalBtnText
                        }
                        tooltipText={`Make internal call to ${user.name}`}
                        disabled={isCallDisabled}
                        onClick={onInternalCallClickHandler}
                      />
                    )}
                  </>
                )}

                {(isOwnAgent || hasOrganizationMemberAccess) && (
                  <PresenceDropdownItem
                    icon="pen"
                    text="Edit agent"
                    onClick={() => {
                      showUserModal({ user });
                    }}
                  />
                )}

                <PresenceDropdownItem
                  icon={
                    <Icon
                      name={isFavorite ? 'star-full' : 'star'}
                      color={isFavorite ? 'orange.300' : 'neutral.400'}
                    />
                  }
                  text={isFavorite ? 'Unfavorite agent' : 'Favorite agent'}
                  onClick={() =>
                    isFavorite
                      ? onRemoveFromFavorites(user.id)
                      : onAddToFavorites(user.id)
                  }
                />

                <Divider />

                {!hasConversationsAssigned ? (
                  <PresenceDropdownItem
                    icon="chat-slash"
                    text="No conversations here"
                    disabled={true}
                  />
                ) : (
                  (Object.keys(conversationsByChannel) as IconNames[]).map(
                    (iconName) => {
                      return conversationsByChannel[iconName]?.map(
                        (item, index) => (
                          <PresenceDropdownItem
                            key={index}
                            icon={iconName}
                            text={`Open #${item.csid}`}
                            onClick={() => handlePresenceLinkClick(item.csid)}
                          />
                        ),
                      );
                    },
                  )
                )}
              </div>
            </>
          ) : null}
          <Popover.Arrow
            style={{ fill: colors.neutral['000'] }}
            width={16}
            height={8}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
